import * as React from "react";

import { StatusGlyph } from "@/components/ui/status-glyph";
import { Tooltip, TipTrigger, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/** A reason with no full stop at its end: an error is a phrase, not a sentence. */
function reasonText(reason: string): string {
  return reason.replace(/\.\s*$/, "");
}

/**
 * A reason as the line shows it: the first sentence, without its full stop, and the detail that follows ("Check postcode. That is
 * not a UK postcode" is the line "Check postcode" and the detail "That is not a UK postcode"). The tooltip has all of it.
 */
function splitReason(reason: string): { head: string; rest: string } {
  const m = /^(.+?[a-z0-9)”’"])\.\s+(?=[A-Z])/.exec(reason);
  return m ? { head: m[1]!, rest: reasonText(reason.slice(m[0].length)) } : { head: reasonText(reason), rest: "" };
}

/**
 * Why a value is not acceptable, said on the label line and never below it, so nothing under it moves: amber, label meta size,
 * right-aligned, one line cut with an ellipsis. The tooltip holds the full words (focus or tap reaches it as well as hover).
 * Spread onto Base UI's `Field.Error` (`render`) and used for a row's label line (`RowReason`).
 */
function ReasonLine({
  className,
  children,
  full,
  ...props
}: React.ComponentProps<"div"> & {
  /** The words the tooltip holds when the line shows only a part of them (several reasons joined on one line). */
  full?: string;
}) {
  const split = typeof children === "string" ? splitReason(children) : null;
  // What the line cuts off is still read out: the control is described by the whole reason.
  const cut = split
    ? split.rest
      ? `. ${split.rest}`
      : full && full !== split.head
        ? full.startsWith(split.head)
          ? full.slice(split.head.length)
          : ` ${full}`
        : ""
    : "";
  const line = split ? (
    <>
      {split.head}
      {cut ? <span className="sr-only">{cut}</span> : null}
    </>
  ) : (
    children
  );
  return (
    <Tooltip>
      <TipTrigger
        render={
          <div
            data-slot="reason-line"
            className={cn("relative min-w-0 truncate text-right text-caption text-warning", className)}
            {...props}
          />
        }
      >
        {line}
      </TipTrigger>
      {/* The line is right-aligned inside a wider box: the tooltip hangs from the box's right end, beside the words, not from its middle. */}
      <TooltipContent variant="rich" align="end" className="max-w-72">
        {full ?? (split ? (split.rest ? `${split.head}. ${split.rest}` : split.head) : children)}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * The amber "!" of a value with a problem, for the eye (a screen reader has the reason as a description): the full words in its
 * tooltip (none when the "!" stands alone). Position it with `className` inside a `relative` box.
 */
function ProblemMark({ reason, className }: { reason?: React.ReactNode; className?: string }) {
  if (!reason)
    return (
      <span data-slot="field-mark" aria-hidden className={cn("absolute flex", className)}>
        <StatusGlyph status="attention" />
      </span>
    );
  return (
    <Tooltip>
      <TooltipTrigger render={<span data-slot="field-mark" aria-hidden className={cn("absolute flex", className)} />}>
        <StatusGlyph status="attention" />
      </TooltipTrigger>
      <TooltipContent variant="rich" className="max-w-72">
        {typeof reason === "string" ? reasonText(reason) : reason}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * A field with its reason on the same line, from `sm` up: the field keeps at least 14rem, the reason takes the rest,
 * right-aligned, one line cut with an ellipsis, the full words in its tooltip. Below `sm` the reason is said elsewhere.
 * Without a `reason` the wrapper is still here and lays out as a plain block, so a field never remounts (and loses the
 * caret) when a problem appears under the typing.
 */
function ReasonBeside({ reason, full, children }: { reason?: string; full?: string; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        reason && "sm:grid sm:grid-cols-[minmax(14rem,1fr)_minmax(0,max-content)] sm:items-center sm:gap-x-3"
      )}
    >
      {children}
      {reason ? (
        <ReasonLine className="hidden sm:block" full={full}>
          {reason}
        </ReasonLine>
      ) : null}
    </div>
  );
}

export { ReasonLine, ReasonBeside, ProblemMark, reasonText, splitReason };
